import {
  DisplayContext,
  PartialPricingEngineOverrides,
  ProfitItemCellData,
  ProfitItemData,
} from '@frontend/api';

import {
  getProfitItem as getProfitItemFromStore,
  getCellsForProfitItem,
} from 'modules/quotes/quoteItems/data/selectors';

import { PAGE_REQUEST } from 'store/app/actions';
import { RootState } from 'store/configureStore';
import { ActionWithPayload } from 'store/types';

import { types } from './actions';

type ProfitItemModalState = {
  profitItemId: number | null;
  cells: ProfitItemCellData[];
  displayContext: DisplayContext[];
  isReadOnly: boolean;
  isOpen: boolean;
  overrides: PartialPricingEngineOverrides;
  liveProfitItem: ProfitItemData | null;
};

const initialState: ProfitItemModalState = {
  profitItemId: null,
  cells: [],
  displayContext: [],
  isReadOnly: false,
  isOpen: false,
  overrides: {},
  liveProfitItem: null,
};

const reducer = (
  state = initialState,
  action: ActionWithPayload<string, any>,
) => {
  const { payload } = action;
  switch (action.type) {
    case PAGE_REQUEST:
      return {
        ...initialState,
      };
    case types.OPEN_PROFIT_ITEM_MODAL:
      return {
        ...state,
        profitItemId: payload.profitItem.id,
        cells: payload.cells,
        displayContext: payload.profitItem.displayContext,
        isOpen: true,
        isReadOnly: payload.isReadOnly,
        overrides: payload.profitItem.overrides,
      };
    case types.CLOSE_PROFIT_ITEM_MODAL:
      return {
        ...state,
        profitItemId: null,
        cells: [],
        displayContext: [],
        isOpen: false,
        isReadOnly: false,
        overrides: {},
        liveProfitItem: null,
      };
    case types.LIVE_PROFIT_ITEM_PREVIEW_REQUEST:
      return {
        ...state,
        overrides: payload.overrides,
      };
    case types.LIVE_PROFIT_ITEM_PREVIEW_SUCCESS:
      return {
        ...state,
        cells: payload.profitItemCells,
        displayContext: payload.displayContext,
        liveProfitItem: payload,
      };
    case types.RECEIVE_OVERRIDES:
      return {
        ...state,
        overrides: payload.nextOverrides,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): ProfitItemModalState =>
  state.modules.quotes.profitItemModal;

export const getProfitItemId = (state: RootState) =>
  getScope(state).profitItemId;

export const getProfitItem = (state: RootState) => {
  const profitItemId = getProfitItemId(state);
  return profitItemId ? getProfitItemFromStore(state, profitItemId) : undefined;
};

export const getCurrentOverrides = (state: RootState) =>
  getScope(state).overrides;

export const getDisplayContext = (state: RootState) =>
  getScope(state).displayContext;

export const getGlobalProfitItemCells = (state: RootState) =>
  getCellsForProfitItem(state, getProfitItemId(state));

export const getIsProfitItemModalOpen = (state: RootState) =>
  getScope(state).isOpen;

export const getIsReadOnly = (state: RootState) => getScope(state).isReadOnly;

export const getLocalProfitItemCells = (state: RootState) =>
  getScope(state).cells;

export const getLiveProfitItem = (state: RootState) =>
  getScope(state).liveProfitItem;
